// noinspection CssUnusedSymbol,DuplicatedCode

import {
  CookieConsent,
  CoreModalDialog,
  CoreSidebarRight,
  CoreToaster,
  IframeOtp,
  LivenessCheckModal,
  MaintenanceModal,
  OtpNew
} from '@components'
import AppLayout from '@components/AppLayout'
import CoreCoinLoader from '@components/CoreCoinLoader'
import type { CoreModalDialogOptions } from '@components/CoreModalDialog/CoreModalDialog.types'
import { prepareOtpTitle } from '@components/Otp/Otp.helper'
import HeaderContextProvider from '@context/HeaderContext'
import { type LivenessCallback, LivenessContext } from '@context/LivenessContext'
import { ThemeContext } from '@context/ThemeContext'
import { useDocumentRequestPopup } from '@hooks'
import ErrorIcon from '@images/icons/error.svg?react'
import CallRequestFab from '@modules/callRequest/components/CallRequestFab/CallRequestFab'
import SupportFab from '@modules/support/components/SupportFab/SupportFab'
import { adminRoutes, logoutAnd404Routes, nonProtectedRoutes, staffRoutes } from '@routes'
import settings from '@settings'
import {
  PrivateRoute,
  PublicRoute,
  paths,
  selectCurrentUserRole,
  selectCurrentUserStatus,
  selectMerchantCapabilities,
  useAllFeatureFlags,
  useAppInitializerQry,
  useAppSelector,
  useAuthQry,
  useCardAuth,
  useLazyErrorLoggingQuery
} from 'mmfintech-backend-api'
import type { RouteDefinition } from 'mmfintech-backend-api/types'
import {
  FeatureFlagDebugBanner,
  GlobalContextProvider,
  isValidObject,
  isValidString,
  OtpContext,
  tr
} from 'mmfintech-commons'
import { MMGlobalSettingsProvider } from 'mmfintech-portal-commons'
import type { MMGlobalSettings } from 'mmfintech-portal-commons/types'
import { type ReactNode, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CookiesProvider, useCookies } from 'react-cookie'
import { ErrorBoundary } from 'react-error-boundary'
import { withTranslation } from 'react-i18next'
import { matchPath, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import theme from './theme'
import Error404 from './views/auth/Error404'

type ShowModalProps = {
  header?: ReactNode
  content: ReactNode
  options?: CoreModalDialogOptions
}

const pathExistsInProtectedRoutes = (path: string): boolean => {
  return [...nonProtectedRoutes, ...logoutAnd404Routes].some((route: RouteDefinition) => {
    const test = route.check
      ? isValidObject(matchPath(path, { path: route.check, exact: false, strict: false }))
      : false
    return route.path === path || test
  })
}

const AppInner = () => {
  useAppInitializerQry(settings.languages)

  const [cookies, setCookie] = useCookies(['cookie.consent'])
  const [cookieConsent, setCookieConsent] = useState(null)
  const appContainerRef = useRef(null)

  const aYearFromNow = new Date()
  aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1)

  const history = useHistory()
  const { darkTheme } = useContext(ThemeContext)

  const { flags: debugFlags, error: debugFlagsError } = useAllFeatureFlags()
  const { isAuthFetching, authSucceeded } = useAuthQry()
  const { fetchCardAuthToken } = useCardAuth()

  const capabilities = useAppSelector(selectMerchantCapabilities)
  const customerRole = useAppSelector(selectCurrentUserRole)
  const userStatus = useAppSelector(selectCurrentUserStatus)

  const isLoggedIn = useMemo(() => userStatus === 'LOGGED_IN', [userStatus])

  const routes = () => {
    switch (customerRole) {
      case 'STAFF':
        return staffRoutes
      default:
        return adminRoutes
    }
  }

  const queryChallenge = useAppSelector(state => state.challenge)
  const queryLiveness = useAppSelector(state => state.liveness)

  useEffect(() => {
    setCookieConsent(cookies['cookie.consent'])
  }, [cookies])

  // (func: () => void) => void
  const [otpOnError, setOtpOnError] = useState(null)
  const [otpOnSuccess, setOtpOnSuccess] = useState(null)

  // Deferred liveness callbacks — mirror of the OTP ones (see LivenessContext).
  const [livenessOnSuccess, setLivenessOnSuccess] = useState<LivenessCallback | null>(null)
  const [livenessOnError, setLivenessOnError] = useState<LivenessCallback | null>(null)

  // global center modal state
  const [modalHeader, setModalHeader] = useState<ReactNode | null>(null)
  const [modalContent, setModalContent] = useState<ReactNode | null>(null)
  const [modalOptions, setModalOptions] = useState<CoreModalDialogOptions | null>(null)
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  // global right modal state
  const [sidebarRightHeader, setSidebarRightHeader] = useState<ReactNode>(null)
  const [sidebarRightContent, setSidebarRightContent] = useState<ReactNode>(null)
  const [sidebarRightOptions, setSidebarRightOptions] = useState<CoreModalDialogOptions | null>(null)
  const [sidebarRightVisible, setSidebarRightVisible] = useState<boolean>(false)

  const [iframeMessage, setIframeMessage] = useState(null)
  // const [showIframeAsOverlay, setShowIframeAsOverlay] = useState<boolean>(false)

  const resetIframeMessage = useCallback(() => setIframeMessage(null), [setIframeMessage])

  const modalHide = useCallback(() => {
    setModalVisible(false)
    setModalContent(null)
    setModalOptions(null)
    setModalHeader(null)
  }, [])

  const modalShow = useCallback(({ header, content, options }: ShowModalProps) => {
    setModalHeader(header ?? null)
    setModalContent(content ?? null)
    setModalOptions(options ?? null)
    setModalVisible(true)
  }, [])

  const sidebarRightHide = useCallback(() => {
    setSidebarRightVisible(false)
    setSidebarRightContent(null)
    setSidebarRightOptions(null)
    setSidebarRightHeader(null)
  }, [])

  const sidebarRightShow = useCallback(({ header, content, options }: ShowModalProps) => {
    setSidebarRightHeader(header ?? null)
    setSidebarRightContent(content ?? null)
    setSidebarRightOptions(options ?? null)
    setSidebarRightVisible(true)
  }, [])

  const globalContext = useMemo(
    () => ({
      modalHide,
      modalShow,
      sidebarRightHide,
      sidebarRightShow,
      iframeMessage,
      setIframeMessage,
      resetIframeMessage
    }),
    [modalHide, modalShow, sidebarRightHide, sidebarRightShow, iframeMessage, resetIframeMessage]
  )

  const otpContext = useMemo(
    () => ({
      otpOnSuccess,
      setOtpOnSuccess,
      otpOnError,
      setOtpOnError
    }),
    [otpOnError, otpOnSuccess]
  )

  const livenessContext = useMemo(
    () => ({
      livenessOnSuccess,
      setLivenessOnSuccess,
      livenessOnError,
      setLivenessOnError
    }),
    [livenessOnSuccess, livenessOnError]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only on queryChallenge change
  useEffect(() => {
    if (typeof queryChallenge === 'object' && 'challengeId' in queryChallenge) {
      modalShow({
        header: prepareOtpTitle(queryChallenge, userStatus),
        content: <OtpNew />,
        options: {
          transparent: false,
          closeOnClickOutside: false
        }
      })
    } else if (!(typeof queryLiveness === 'object' && 'livenessCheckId' in queryLiveness)) {
      // Don't tear down an active liveness modal — the liveness effect owns the
      // modal while a check is in progress (challenge clears fire during init).
      modalVisible && modalHide()
    }
  }, [queryChallenge])

  // The liveness check is a redirect-based second security layer that arrives
  // after the challenge (never together). When it appears in any response the
  // liveness slice is populated; show the modal so the user can complete the
  // check on the partner site and see its status. The modal clears the slice
  // itself when the user is done.
  //
  // We key off `modalVisible` (not just `queryLiveness`) so the modal re-opens
  // whenever there's an active check but the global modal got torn down — e.g.
  // an identical poll/response that doesn't change the slice reference, or a
  // dismissal that didn't clear the slice. A challenge takes precedence (the two
  // never legitimately coexist). Once the user finishes, `handleClose` clears
  // the slice, so this can't re-open it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: modalShow/tr are stable; re-run on slice/visibility change
  useEffect(() => {
    const hasLiveness = typeof queryLiveness === 'object' && 'livenessCheckId' in queryLiveness
    const hasChallenge = typeof queryChallenge === 'object' && 'challengeId' in queryChallenge
    if (hasLiveness && !hasChallenge && !modalVisible) {
      modalShow({
        header: tr('FRONTEND.LIVENESS.TITLE', 'Additional verification required'),
        content: <LivenessCheckModal />,
        options: {
          transparent: false,
          closeOnClickOutside: false,
          hideCloseIcon: true
        }
      })
    }
  }, [queryLiveness, queryChallenge, modalVisible])

  useEffect(() => {
    window.addEventListener('storage', e => {
      if (e.key === 'cookieTimestamp' && e.newValue !== e.oldValue) {
        history.go(0)
      }
    })
  }, [])

  useDocumentRequestPopup(isLoggedIn, modalShow)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkTheme ? 'dark' : 'light')
  }, [darkTheme])

  useEffect(() => {
    const cardAuthTokenVerification = localStorage.getItem('cardAuthTokenVerification')
    const shouldReAuth =
      !cardAuthTokenVerification ||
      Math.round((Date.now() - new Date(Number(cardAuthTokenVerification)).getTime()) / 1000) > 60 * 5
    const shouldAuthCards = authSucceeded || (isLoggedIn && shouldReAuth)
    if (shouldAuthCards && capabilities.enableCardIssuing) {
      fetchCardAuthToken()
    }
  }, [authSucceeded, capabilities, isLoggedIn])

  useEffect(() => {
    window.addEventListener('storage', e => {
      if (e.key === 'thekingdombank-id' && e.newValue !== e.oldValue) {
        history.go(0)
      }
    })
    const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent'
    const eventCallback = window[eventMethod]
    const messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message'
    const allowedOrigins = [
      'http://localhost:3001',
      'https://cards-dev.thekingdombank.com',
      'https://cards-sandbox.thekingdombank.com',
      'https://cards.thekingdombank.com'
    ]
    eventCallback(
      messageEvent,
      (e: any) => {
        if (allowedOrigins.includes(e?.origin) && appContainerRef.current) {
          const key = e.message ? 'message' : 'data'
          const data = e[key]

          switch (data.type) {
            case 'IFRAME_SIZE': {
              const iframeElement = Array.from(document.querySelectorAll('iframe')).find(
                frame => frame.contentWindow === e.source
              )
              if (iframeElement) iframeElement.style.height = `${data?.value}px`
              break
            }
            // case 'OPEN_OVERLAY':
            //   setShowIframeAsOverlay(true)
            //   break
            // case 'HIDE_OVERLAY':
            //   setShowIframeAsOverlay(false)
            //   break
            case 'DETAILS_LOADED':
              setIframeMessage('DETAILS_LOADED')
              break
            case 'DETAILS_ERROR':
              setIframeMessage('CARD_DETAILS_ERROR')
              break
            case 'CARD_WITHDRAWAL_PREVIEW':
              setIframeMessage('CARD_WITHDRAWAL_PREVIEW')
              break
            case 'CARD_WITHDRAWAL_BACK':
              setIframeMessage('CARD_WITHDRAWAL_BACK')
              break
            case 'OPEN_IFRAME_MODAL':
              setIframeMessage('OPEN_IFRAME_MODAL')
              modalShow({
                content: <IframeOtp type={'cardDetails'} operationId={data?.value} />,
                header: tr('FRONTEND.MODAL.CONFIRM_OPERATION', 'Confirm operation'),
                options: { closeOnClickOutside: false }
              })
              break
            case 'OPEN_WITHDRAWAL_OTP':
              setIframeMessage('OPEN_IFRAME_MODAL')
              modalShow({
                content: (
                  <IframeOtp
                    type={'cardWithdrawal'}
                    operationId={data?.value?.id}
                    challenge={data?.value?.type}
                    sentTo={data?.value?.sentTo}
                  />
                ),
                header:
                  prepareOtpTitle({ twoFactorType: data?.value?.type }, 'LOGGED_IN') ||
                  tr('FRONTEND.MODAL.CONFIRM_OPERATION', 'Confirm operation'),
                options: { closeOnClickOutside: false }
              })
              break
            case 'WITHDRAWAL_COMPLETED':
              modalHide()
              history.push({
                pathname: paths.banking.finalStatus.success('PAY_TO_CARD'),
                state: {
                  status: data?.value,
                  message: ['PROCESSED', 'PENDING', 'SCHEDULED', 'INITIAL', 'CONFIRMED'].includes(data?.value)
                    ? tr('FRONTEND.SEND.SUCCESS', 'Successfully Sent!')
                    : tr('FRONTEND.SEND.FAIL', 'Send failed!')
                }
              })
              break
            case 'CHALLENGE_SOLVED':
              setIframeMessage('REVEAL_DETAILS')
              modalHide()
              break
          }
        }
      },
      false
    )
  }, [])

  const mmGlobalSettings: Partial<MMGlobalSettings> = {
    buttonLoadingColor: '#888888',
    buttonLoadingStyle: 'ThreeDots',
    buttonLoadingHeight: 8
  }

  const location = useLocation()

  if (isAuthFetching && !pathExistsInProtectedRoutes(location?.pathname)) {
    return <div className='preloader-wrapper'>{CoreCoinLoader && <CoreCoinLoader />}</div>
  }

  return (
    <MMGlobalSettingsProvider settings={mmGlobalSettings}>
      <ThemeProvider theme={theme}>
        <MaintenanceModal />
        <GlobalContextProvider context={globalContext}>
          <OtpContext.Provider value={otpContext}>
            <LivenessContext.Provider value={livenessContext}>
              <HeaderContextProvider>
                <AppLayout
                  menuVisible={isLoggedIn && !pathExistsInProtectedRoutes(location.pathname)}
                  ref={appContainerRef}
                >
                  <Switch>
                    {nonProtectedRoutes.map(definition => (
                      <PublicRoute key={definition.path} exact {...definition} />
                    ))}
                    {routes().map(definition => (
                      <PrivateRoute key={definition.path} exact {...definition} />
                    ))}
                    <Route path='*' component={Error404} />
                  </Switch>
                </AppLayout>

                {isValidObject(cookieConsent) ? null : <CookieConsent setCookie={setCookie} />}

                <CoreModalDialog
                  header={modalHeader}
                  content={modalContent}
                  options={modalOptions}
                  visible={modalVisible}
                  onClose={modalHide}
                />

                <CoreSidebarRight
                  header={sidebarRightHeader}
                  content={sidebarRightContent}
                  options={sidebarRightOptions}
                  visible={sidebarRightVisible}
                  onClose={sidebarRightHide}
                />

                {isLoggedIn && <CallRequestFab />}
                <SupportFab />
                <CoreToaster />
                <FeatureFlagDebugBanner flags={debugFlags} error={debugFlagsError} />
              </HeaderContextProvider>
            </LivenessContext.Provider>
          </OtpContext.Provider>
        </GlobalContextProvider>
      </ThemeProvider>
    </MMGlobalSettingsProvider>
  )
}

function ErrorFallback({ error, resetErrorBoundary }) {
  const isDynamicModuleError = isValidString(error?.message)
    ? error.message.includes('Failed to fetch dynamically imported module')
    : false
  return (
    <div className='alert-wrapper'>
      <div className='alert-container'>
        <ErrorIcon />
        <p>
          {isDynamicModuleError
            ? tr(
                'FRONTEND.APP.THERE_MIGHT_BE_A_NEW_VERSION_OF_THE_APPLICATION',
                'There might be a new version of the application'
              )
            : tr('FRONTEND.APP.AN_UNEXPECTED_ERROR_OCCURRED', 'An unexpected error occurred')}
        </p>
        <button type='button' onClick={() => resetErrorBoundary(isDynamicModuleError)}>
          {isDynamicModuleError
            ? tr('FRONTEND.APP.RELOAD_PAGE', 'Reload page')
            : tr('FRONTEND.APP.GO_BACK_TO_THE_HOME_PAGE', 'Go back to the home page')}
        </button>
      </div>
    </div>
  )
}

const ThisApp = withTranslation()(AppInner)

const App = () => {
  const [logError] = useLazyErrorLoggingQuery()

  return (
    <CookiesProvider>
      <Suspense fallback={<div className='preloader-wrapper'>{CoreCoinLoader && <CoreCoinLoader />}</div>}>
        <ErrorBoundary
          onError={(err, componentStack) => {
            console.log('[ErrorBoundary] Caught error:', err)
            console.log('[ErrorBoundary] Component stack:', componentStack?.componentStack || componentStack)
            try {
              const reloadTimestamp = localStorage.getItem('dynamicImportTimestamp')
              if (
                (err as any)?.message?.includes('dynamically imported module') &&
                (!reloadTimestamp || Date.now() - Number(reloadTimestamp) > 60_000)
              ) {
                localStorage.setItem('dynamicImportTimestamp', Date.now().toString())
                return window.location.reload()
              }
            } catch (error) {
              // handles cases in which the browser has no access to the storage
              console.error(error)
            }
            return logError({
              level: 'ERROR',
              componentStack: componentStack?.componentStack || (componentStack as string),
              message: `${err.toString()} | ${window?.location?.pathname}`
            })
          }}
          onReset={isDynamicModuleError => {
            if (isDynamicModuleError) {
              window.location.reload()
            } else {
              window.location.replace('/')
            }
          }}
          FallbackComponent={ErrorFallback}
        >
          <ThisApp />
        </ErrorBoundary>
      </Suspense>
    </CookiesProvider>
  )
}

export default App
